import {
  Box,
  Button,
  ButtonBase,
  Card,
  CardActionArea,
  CardMedia,
  Stack,
  Typography,
} from '@mui/material'
import { MouseEventHandler, useState } from 'react'
import { EmphasizedNumber, Fade, Icons, PointDisplay } from 'utoniq-ui'

import {
  Artist,
  Collection,
  Gacha,
  Image,
  Item,
  ItemImageAreaFragment,
  ItemNftChain,
  Maybe,
  TradableUsersConnectionFragment,
  UserPaymentType,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import {
  ItemImageArea,
  ReplyFormValues,
  SupplementLinkArea,
} from '@/components/organisms'
import { HorizontalList, Steps } from '@/components/organisms/functionals'
import { NotFound } from '@/components/pages/Errors/NotFound'
import { SkeletonLoader } from '@/components/pages/OwnedItemDetail/template/components/SkeletonLoader'
import { ArtistThemeProvider, styleSets } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { Cell, CellFrame, Template } from '@/core/architecture'
import {
  individualHiddenItemInfo,
  individualHideWallet,
} from '@/core/individual'
import { usePrivateAction } from '@/lib/privateAction'

import { TradeRequestUserSelect } from './TradeRequestUserSelect'

export * from './fragments'

export type State = {
  loading: boolean
  loadingTradableUsers: boolean
  fetching: boolean
  isLoggedIn: boolean
  hasNextPage: boolean
  item: Maybe<
    Pick<
      Item,
      | 'description'
      | 'commentCount'
      | 'isPublished'
      | 'price'
      | 'formattedPrice'
      | 'paymentType'
    > & {
      issuedArtist?: Maybe<Pick<Artist, 'id'>>
      collection?: Maybe<Pick<Collection, 'isPublishOnStore'>>
      gachas?: Maybe<
        (Pick<Gacha, 'id' | 'name'> & {
          thumbnail: Pick<Image, 'uri' | 'mediumUri'>
        })[]
      >
      nft?: Maybe<{ chains?: Maybe<Array<Pick<ItemNftChain, 'chainName'>>> }>
    } & ItemImageAreaFragment
  >
  tradableUsers: Maybe<TradableUsersConnectionFragment>
}

type CollectionItemDetailProps = {
  item: State['item']
  loading: State['loading']
  isLoggedIn: State['isLoggedIn']
  onNextStep: MouseEventHandler<HTMLButtonElement>
}

type CollectionItemDetailCellProps = {
  loading: boolean
  item: State['item']
  onNextStep: MouseEventHandler<HTMLButtonElement>
}

export type Action = {
  onReply: (values: ReplyFormValues) => Promise<void>
  onFetchMore: () => Promise<void>
  onGetTradableUsers: () => Promise<void>
}

export const CollectionItemDetailTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onGetTradableUsers,
}) => {
  const { analytics } = useAnalytics()
  const { handleLoginModal } = usePrivateAction()
  const [currentStep, setCurrentStep] = useState<number>(0)

  if (!state.loading && !state.item) {
    return <NotFound useNextRouter={false} />
  }

  /**
   * NFT 停止に向けて、NFTアイテムの詳細ページを表示しない
   * NFT 機能停止後には削除して問題ない
   */
  if (state.item?.nft?.chains) {
    return <NotFound useNextRouter={false} />
  }

  return (
    <ArtistThemeProvider artistId={state.item?.issuedArtist?.id}>
      <Steps sx={{ height: '100%' }} value={currentStep}>
        <CollectionItemDetail
          item={state.item}
          loading={state.loading}
          isLoggedIn={state.isLoggedIn}
          onNextStep={handleLoginModal(() => {
            analytics?.track('click_tradableUsers', {
              applicationKey: config.applicationKey,
              itemId: state.item?.id ?? '#',
            })
            onGetTradableUsers()
            setCurrentStep(1)
          })}
        />
        {!!currentStep && (
          <TradeRequestUserSelect
            loading={state.fetching || state.loadingTradableUsers}
            itemId={state.item?.id ?? ''}
            tradableUsers={state.tradableUsers}
            hasNextPage={state.hasNextPage}
            onFetchMore={onFetchMore}
            onClose={() => setCurrentStep(0)}
          />
        )}
      </Steps>
    </ArtistThemeProvider>
  )
}

const CollectionItemDetail: Cell<CollectionItemDetailProps> = ({
  item,
  loading,
  onNextStep,
}) => {
  const { router, routerNames, routerPages, generatePath } = useRouter<{
    collectionId: string
    itemId: string
  }>()
  return (
    <BaseLayout
      header={{
        back: true,
        defaultBackPage: generatePath(routerPages.CollectionDetail, {
          collectionId: router.query.collectionId ?? '#',
        }),
        type: 'modal',
        /**
         * @todo
         * ヘルプモーダル実装までアイコン隠す
         * https://app.clickup.com/t/1zw7hyb
         */
        //right: <IconButton.Error size="medium" />,
      }}
      sx={{ height: '100dvh' }}
    >
      <Head title={routerNames.CollectionItemDetail} />
      <Fade style={{ height: '100%' }}>
        {loading ? (
          <SkeletonLoader />
        ) : (
          <CollectionItemDetailCell
            loading={loading}
            item={item}
            onNextStep={onNextStep}
          />
        )}
      </Fade>
    </BaseLayout>
  )
}

const CollectionItemDetailCell: Cell<CollectionItemDetailCellProps> = ({
  loading,
  item,
  onNextStep,
}) => {
  const { handleLoginModal } = usePrivateAction()
  const { generatePath, routerPages } = useRouter()
  const { t } = useTranslation('collectionItemDetail')

  return (
    <CellFrame
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: '1 0 auto',
      }}
    >
      <ItemImageArea item={item} loading={loading} noItem>
        {item?.collection?.isPublishOnStore ? (
          <ButtonBase
            sx={{
              bgcolor: 'grey.50',
              height: 56,
              width: 335,
              borderRadius: 1,
              justifyContent: 'space-between',
              px: 4,
              py: 2,
              mt: 6,
            }}
            href={generatePath(routerPages.ArtistStoreItemDetail, {
              artistId: item?.issuedArtist?.id,
              itemId: item?.id,
            })}
          >
            <Typography color="text.primary" variant="subtitle1">
              {t('buyInStore')}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={2}>
              {!individualHideWallet() &&
                (item?.paymentType !== UserPaymentType.Cash ? (
                  <PointDisplay
                    size="medium"
                    point={item?.formattedPrice ?? 0}
                  />
                ) : (
                  <EmphasizedNumber variant="h4">
                    ￥{item?.formattedPrice ?? 0}
                  </EmphasizedNumber>
                ))}
              <Icons.RightArrow fontSize="xSmall" />
            </Stack>
          </ButtonBase>
        ) : (
          <>
            {config.modules.trade && !item?.hideSalesNumber && !item?.sku && (
              <Box sx={{ mt: 6, mb: 2 }}>
                <Button size="medium" variant="outlined" onClick={onNextStep}>
                  {t('checkWhoCanTrade')}
                </Button>
              </Box>
            )}
          </>
        )}
      </ItemImageArea>
      {config.modules.gacha && !loading && !!item?.gachas?.length && (
        <GachasLinkCell
          gachas={item?.gachas as Gacha[]}
          artistId={item?.issuedArtist?.id ?? '#'}
        />
      )}
      <SupplementLinkArea
        transparent
        itemId={item?.id}
        artistId={item?.issuedArtist?.id ?? ''}
        startItems={[
          {
            type: 'itemInfo',
            hidden: individualHiddenItemInfo(),
            href: generatePath(routerPages.OwnedItemInformation, {
              itemId: item?.id ?? '#',
            }),
            onClick: handleLoginModal(),
          },
        ]}
        endItems={[{ type: 'comment', title: item?.commentCount ?? 0 }]}
        pageName="collection_item_detail"
      />
    </CellFrame>
  )
}

type GachaLinkCellProps = {
  gachas?: Maybe<Pick<Gacha, 'id' | 'name' | 'thumbnail'>[]>
  artistId: string
}
const GachasLinkCell: Cell<GachaLinkCellProps> = ({ gachas, artistId }) => {
  const { t } = useTranslation('collectionItemDetail')

  if (!gachas?.length) {
    return null
  }

  return (
    <CellFrame sx={{ pb: 9 }}>
      <HorizontalList
        title={t('gachaLabel', '以下のガチャに含まれています')}
        titleTypographyProps={{ variant: 'overline' }}
        items={gachas}
        renderItem={item => <GachaBoxCell gacha={item} artistId={artistId} />}
      />
    </CellFrame>
  )
}

type GachaBoxCellProps = {
  gacha: Pick<Gacha, 'id' | 'name' | 'thumbnail'>
  artistId: string
}
const GachaBoxCell: Cell<GachaBoxCellProps> = ({ gacha, artistId }) => {
  const { generatePath, routerPages } = useRouter()
  return (
    <CellFrame>
      <Card elevation={0} sx={{ bgcolor: 'transparent', width: 160 }}>
        <CardActionArea
          href={generatePath(routerPages.ArtistGachaDetail, {
            artistId,
            gachaId: gacha.id,
          })}
        >
          <CardMedia
            component="img"
            height="90"
            image={gacha.thumbnail.mediumUri ?? ''}
            sx={{ borderRadius: 1 }}
            alt={gacha.name}
          />
          <Typography variant="subtitle1" sx={styleSets.lineCamp(1)}>
            {gacha.name}
          </Typography>
        </CardActionArea>
      </Card>
    </CellFrame>
  )
}
